import React from 'react';
import T from 'prop-types';
// redux
import { connect } from 'react-redux';
import { compose } from 'recompose';
import { formValueSelector, FieldArray, reduxForm } from 'redux-form';
// components
import { FormattedMessage } from 'react-intl';
import { Link } from 'react-router';
import Field from 'components/Field';
import Button from 'components/Button';
import CircleCheckboxField from 'components/Admin/Farms/Fields/CircleCheckboxField';
import FeedRationMedicationRow from 'components/ReduxFormFields/FeedRationMedicationRow';
// endpoints
import { getFeedTreatmentProducts } from 'endpoints/admin/feedRations';
// constants
import { feedRationFormName, feedRationsPath } from '../constants';
// utils
import classnames from 'classnames/bind';
// styles
import styles from './FeedRationForm.module.scss';

const cn = classnames.bind(styles);

const FeedRationMedicationRows = ({ fields, rows, duplicates, removeLabel }) => (
  <div>
    {fields.map((member, index) => (
      <FeedRationMedicationRow
        key={member}
        member={member}
        selected={rows[index]?.treatment_product}
        optionsGetter={getFeedTreatmentProducts}
        onRemove={() => fields.remove(index)}
        isDuplicate={duplicates[index]}
        removeLabel={removeLabel}
      />
    ))}
  </div>
);

FeedRationMedicationRows.propTypes = {
  fields: T.object.isRequired,
  rows: T.array.isRequired,
  duplicates: T.arrayOf(T.bool).isRequired,
  removeLabel: T.string.isRequired,
};

const FeedRationForm = (
  { handleSubmit, pristine, submitting, edit, name, medicated, medications, array },
  { formatMessage }
) => {
  const rows = medicated ? medications : [];
  const productIds = rows.map((row) => row?.treatment_product?.value);
  const duplicates = productIds.map((productId, index) => !!productId && productIds.indexOf(productId) !== index);
  const hasEmptyRow = productIds.some((productId) => !productId);
  const isMedicationsInvalid = medicated && (!rows.length || hasEmptyRow || duplicates.some(Boolean));
  const isSubmitDisabled = submitting || pristine || !(name || '').trim() || isMedicationsInvalid;
  const removeLabel = formatMessage({ id: 'container.admin.feedRations.removeMedication' });

  const addMedication = () => array.push('medications', {});

  // Checking the box reveals the first (empty) medication row right away
  const onMedicatedChange = (checked) => {
    if (checked && !medications.length) addMedication();
  };

  return (
    <form onSubmit={handleSubmit} className={cn('FeedRationForm')}>
      <div className="row">
        <div className="small-12 medium-8 column">
          <Field
            name="name"
            label={<FormattedMessage id="container.admin.feedRations.rationName" />}
            placeholderKey="general.typeAName"
          />
        </div>
      </div>

      <div className="row">
        <div className="small-12 column">
          <span className={cn('field-label')}>
            <FormattedMessage id="container.admin.feedRations.medicatedFeedRation" />
          </span>
          <CircleCheckboxField
            name="medicated"
            label={<FormattedMessage id="container.admin.feedRations.containsMedication" />}
            onChangeCallback={onMedicatedChange}
          />
        </div>
      </div>

      {medicated && (
        <div className="row">
          <div className={cn('small-12 column', 'medications-section')}>
            <span className={cn('field-label')}>
              <FormattedMessage id="container.admin.feedRations.medications" />
            </span>
            <span className={cn('field-hint')}>
              <FormattedMessage id="container.admin.feedRations.medicationsHint" />
            </span>
            <span className={cn('field-label', 'sub-label')}>
              <FormattedMessage id="container.admin.feedRations.medication" />
            </span>

            <FieldArray
              name="medications"
              component={FeedRationMedicationRows}
              rows={rows}
              duplicates={duplicates}
              removeLabel={removeLabel}
              rerenderOnEveryChange
            />

            <Button type="button" hollow small onClick={addMedication} className={cn('add-medication-btn')}>
              <FormattedMessage id="container.admin.feedRations.addMedication" />
            </Button>
          </div>
        </div>
      )}

      <div className="row mt-20 mb-20">
        <div className="small-12 column">
          <Button primary type="submit" disabled={isSubmitDisabled} className="no-margin">
            {edit
              ? <FormattedMessage id="general.button.saveChanges" />
              : <FormattedMessage id="container.admin.feedRations.createRation" />}
          </Button>
          <Link to={feedRationsPath} className="button btn-link margin-left-1">
            <FormattedMessage id="general.button.cancel" />
          </Link>
        </div>
      </div>
    </form>
  );
};

FeedRationForm.contextTypes = {
  formatMessage: T.func.isRequired,
};

FeedRationForm.propTypes = {
  handleSubmit: T.func.isRequired,
  array: T.object.isRequired,
  pristine: T.bool,
  submitting: T.bool,
  edit: T.bool,
  name: T.string,
  medicated: T.bool,
  medications: T.array,
};

FeedRationForm.defaultProps = {
  medications: [],
};

const selector = formValueSelector(feedRationFormName);

const enhance = compose(
  connect((state) => ({
    name: selector(state, 'name'),
    medicated: selector(state, 'medicated'),
    medications: selector(state, 'medications'),
  })),
);

export default reduxForm({
  form: feedRationFormName,
  enableReinitialize: true,
})(enhance(FeedRationForm));
